import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
    Badge, Button, Card, EmptyState, LoadingList, SectionHeader, cx,
} from '@timely/ui';
import {
    ACTIVITY_LABELS, DEFAULT_SORT, OUTCOME_CHOICES, bookRow, chequeState, draftProblems,
    emptyDraft, filtersFromParams, followUpCall, holds, isChequeActive, outcomeChoice,
    promiseState, suggestedNextFollowUp, chequeBasis,
    type ActivityEntry, type BookSort, type Cheque, type Customer, type FollowUpDraft,
    type FollowUpOutcome,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { useFormatters } from '../book/useFormatters';
import {
    loadMoreActivity, recordFollowUp, setUrgent, useCustomerDetail, useNeighbours,
    type Neighbours,
} from '../book/useBook';
import { AiReportCard } from '../ai/AiReportCard';

/**
 * One account: where it stands, what was said, and what to do next.
 *
 * Reached by its id, and the id confers nothing. `customers_read` applies
 * `can_see_customer()`, so an account in another company, an account outside
 * this person's scope, and an account that does not exist all come back the
 * same way — and this screen says the same thing for all three. Distinguishing
 * them would confirm which ids are real.
 */
export function CustomerDetail() {
    const context = useActiveWorkspace();
    const { customerId = '' } = useParams();
    const [params] = useSearchParams();
    const { result, loading, reload } = useCustomerDetail(context, customerId);

    // Previous and next come from the same filtered, authorised list the person
    // arrived from — never from "all customers", which would be a way to walk
    // into an account they may not open.
    const neighbours = useNeighbours(
        context.workspace.id,
        customerId ?? '',
        { filters: filtersFromParams(params), sort: (params.get('sort') as BookSort) ?? DEFAULT_SORT },
    );

    // The book's own state, so "Back to the book" returns to the list as it was.
    const backTo = `/w/${context.workspace.slug}/book${params.toString() ? `?${params}` : ''}`;

    if (loading || !result) {
        return (
            <Page>
                <Card className="overflow-hidden"><LoadingList label="Loading the account" rows={6} /></Card>
            </Page>
        );
    }

    if (result.status === 'not-available') {
        return (
            <Page>
                <Card className="px-7 py-10 text-center" role="alert">
                    <h2 className="text-[20px] font-bold text-label tracking-[-0.02em]">
                        That account is not available
                    </h2>
                    <p className="text-[14px] text-label-2 mt-3 leading-relaxed max-w-md mx-auto">
                        It may belong to another company, it may not be one of yours to work, or it
                        may no longer exist. If you think you should be able to see it, ask whoever
                        assigns accounts here.
                    </p>
                    <div className="mt-7">
                        <Link to={backTo} className="text-accent font-semibold hover:underline">
                            Back to the book
                        </Link>
                    </div>
                </Card>
            </Page>
        );
    }

    if (result.status === 'error') {
        return (
            <Page>
                <FormNotice tone="error">{result.message}</FormNotice>
                <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
            </Page>
        );
    }

    return (
        <Account
            detail={result.detail}
            backTo={backTo}
            search={params.toString()}
            neighbours={neighbours}
            onChanged={reload}
        />
    );
}

/* -------------------------------------------------------------------------- */

function Account({
    detail, backTo, search, neighbours, onChanged,
}: {
    detail: { customer: Customer; updatedAt: string; activity: ActivityEntry[]; cheques: Cheque[]; hasMoreActivity: boolean };
    backTo: string;
    /** The list's own state, carried into the previous/next links. */
    search: string;
    /** Where this account sits in the list somebody arrived from, if it does. */
    neighbours: Neighbours | null;
    onChanged: () => Promise<void>;
}) {
    const context = useActiveWorkspace();
    const { money, date, count, today } = useFormatters();
    const { customer, updatedAt } = detail;
    // The company's day, so this account's badge says what the book said.
    const row = bookRow(customer, today);

    const canRecord = holds(context, 'followups.record');
    const canEdit = holds(context, 'customers.edit');

    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const [busy, setBusy] = useState(false);
    const [entries, setEntries] = useState<ActivityEntry[] | null>(null);
    const [moreAvailable, setMoreAvailable] = useState(detail.hasMoreActivity);

    const thread = entries ?? detail.activity;

    const activeCheques = detail.cheques.filter(c => isChequeActive(chequeState(c, today)));

    return (
        <Page>
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
                <Link to={backTo} className="text-[13px] text-accent font-semibold hover:underline">
                    ← Back to the book
                </Link>
                {neighbours && <Walk neighbours={neighbours} slug={context.workspace.slug} search={search} />}
            </div>

            <SectionHeader
                title={customer.company}
                subtitle={[customer.sourceKey, customer.city, customer.category].filter(Boolean).join(' · ')}
                actions={canEdit ? (
                    <Button
                        size="sm"
                        variant={customer.isUrgent ? 'primary' : 'secondary'}
                        disabled={busy}
                        aria-pressed={customer.isUrgent}
                        onClick={() => {
                            setBusy(true);
                            setNotice(null);
                            void setUrgent(customer.id, context.workspace.id, !customer.isUrgent)
                                .then(async () => {
                                    await onChanged();
                                    setNotice({
                                        tone: 'ok',
                                        text: customer.isUrgent ? 'No longer marked urgent.' : 'Marked urgent.',
                                    });
                                })
                                .catch((error: Error) => setNotice({ tone: 'error', text: error.message }))
                                .finally(() => setBusy(false));
                        }}
                    >
                        {customer.isUrgent ? 'Urgent' : 'Mark urgent'}
                    </Button>
                ) : undefined}
            />

            {notice && <div className="mt-4"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}

            <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="min-w-0">
                    <Card className="px-5 py-5">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <div className="text-[12.5px] text-label-3">Outstanding</div>
                                <div className="text-[26px] font-bold text-label num tabular-nums tracking-[-0.02em]">
                                    {money.exact(customer.total)}
                                </div>
                                <div className="text-[12.5px] text-label-3 mt-0.5">
                                    {customer.totalType === 'Cr'
                                        ? 'In credit — we are holding their money'
                                        : 'Owed to us'}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {customer.isUrgent && <Badge tone="dang">Urgent</Badge>}
                                <Badge tone={row.rank === 'Bad' ? 'dang' : row.rank === 'Late' ? 'warn' : 'pos'}>
                                    {row.rank === 'Bad' ? 'Bad debt' : row.rank}
                                </Badge>
                                {row.settled && <Badge tone="pos">Settled</Badge>}
                            </div>
                        </div>

                        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {([
                                ['1–45 days', row.overdue.a1],
                                ['46–90 days', row.overdue.a2],
                                ['91–135 days', row.overdue.a3],
                                ['Over 135 days', row.overdue.a4],
                            ] as const).map(([label, amount]) => (
                                <div key={label} className="rounded-[10px] bg-card-2 px-3 py-2.5">
                                    <div className="text-[11.5px] text-label-3">{label}</div>
                                    <div className="text-[14px] font-semibold text-label num tabular-nums">
                                        {money.compact(amount)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {row.overdue.over90 > 0 && (
                            <p className="text-[13px] text-dang mt-3">
                                <strong className="num">{money.exact(row.overdue.over90)}</strong> has been
                                outstanding for more than 90 days.
                            </p>
                        )}
                        {customer.totalType === 'Cr' && (
                            <p className="text-[13px] text-label-3 mt-3">
                                This account is in credit, so nothing on it is overdue — whatever the
                                ageing columns say.
                            </p>
                        )}
                    </Card>

                    {canRecord ? (
                        <FollowUpForm
                            customerId={customer.id}
                            workspaceId={context.workspace.id}
                            updatedAt={updatedAt}
                            busy={busy}
                            onBusy={setBusy}
                            onDone={async (message) => {
                                setEntries(null);
                                await onChanged();
                                setNotice({ tone: 'ok', text: message });
                            }}
                            onError={message => setNotice({ tone: 'error', text: message })}
                        />
                    ) : (
                        <Card className="mt-5 px-5 py-5">
                            <p className="text-[13.5px] text-label-3">
                                You can read this account’s history but not add to it.
                            </p>
                        </Card>
                    )}

                    <div className="mt-9">
                        <SectionHeader title="History" subtitle="Newest first. Nobody edits what a colleague wrote." />
                    </div>
                    <Card className="mt-4 overflow-hidden">
                        {thread.length === 0 ? (
                            <EmptyState
                                title="Nothing recorded yet"
                                hint={canRecord
                                    ? 'Record what happens when you ring, and whoever picks this account up next will know what was tried.'
                                    : 'Nobody has written anything about this account yet.'}
                            />
                        ) : (
                            <ul className="divide-y divide-separator">
                                {thread.map(entry => (
                                    <ActivityRow key={entry.id} entry={entry} thread={thread} />
                                ))}
                            </ul>
                        )}
                    </Card>

                    {moreAvailable && (
                        <div className="mt-4 text-center">
                            <Button
                                variant="secondary"
                                disabled={busy}
                                onClick={() => {
                                    setBusy(true);
                                    void loadMoreActivity(context.workspace.id, customer.id, thread.length)
                                        .then(more => {
                                            setEntries([...thread, ...more.entries]);
                                            setMoreAvailable(more.hasMore);
                                        })
                                        .catch((error: Error) => setNotice({ tone: 'error', text: error.message }))
                                        .finally(() => setBusy(false));
                                }}
                            >
                                Show older
                            </Button>
                        </div>
                    )}
                </div>

                <div className="min-w-0 space-y-5">
                    <Card className="px-5 py-5">
                        <h3 className="text-[14px] font-bold text-label mb-3">Next follow-up</h3>
                        {customer.followUpDate ? (
                            <>
                                <div className="text-[15px] font-semibold text-label">
                                    <time dateTime={new Date(customer.followUpDate).toISOString()}>
                                        {date.long(customer.followUpDate)}
                                    </time>
                                </div>
                                <div className="text-[12.5px] text-label-3 mt-0.5">
                                    {row.daysToFollowUp === 0 ? 'Today'
                                        : row.daysToFollowUp !== null && row.daysToFollowUp < 0
                                            ? `${count(Math.abs(row.daysToFollowUp))} days overdue`
                                            : `in ${count(row.daysToFollowUp ?? 0)} days`}
                                </div>
                            </>
                        ) : (
                            <p className="text-[13.5px] text-label-3">None set.</p>
                        )}

                        {customer.forecastAmount ? (
                            <p className="text-[13px] text-label-2 mt-3">
                                Expecting <strong className="num">{money.exact(customer.forecastAmount)}</strong>
                                {customer.forecastDate && <> by {date.long(customer.forecastDate)}</>}.
                            </p>
                        ) : null}
                    </Card>

                    <Card className="px-5 py-5">
                        <h3 className="text-[14px] font-bold text-label mb-3">Contact</h3>
                        <Detail label="Person" value={customer.contactPerson} />
                        <Detail label="Phone" value={customer.contactNumber} />
                        <Detail label="Email" value={customer.email} />
                        <Detail label="City" value={[customer.city, customer.state].filter(Boolean).join(', ')} />
                        <Detail label="Address" value={customer.address} />
                    </Card>

                    <Card className="px-5 py-5">
                        <h3 className="text-[14px] font-bold text-label mb-3">Account</h3>
                        <Detail label="Customer code" value={customer.sourceKey} mono />
                        <Detail label="Tax identifier" value={customer.taxId} mono />
                        <Detail label="Category" value={customer.category} />
                        <Detail
                            label="Credit limit"
                            value={customer.creditLimit ? money.exact(customer.creditLimit) : ''}
                        />
                        <Detail
                            label="Payment terms"
                            value={customer.paymentTermsDays ? `${count(customer.paymentTermsDays)} days` : ''}
                        />
                        {/* The code the sheet carried, recorded and never acted on: matching
                            it to a member is the reconciliation queue, which is not built. */}
                        <Detail label="Owner code in the sheet" value={customer.importedOwnerCode} mono />
                    </Card>

                    {holds(context, 'cheques.view') && (
                        <Card className="px-5 py-5">
                            <h3 className="text-[14px] font-bold text-label mb-3">Cheques</h3>
                            {detail.cheques.length === 0 ? (
                                <p className="text-[13.5px] text-label-3">None recorded.</p>
                            ) : (
                                <>
                                    <p className="text-[12.5px] text-label-3 mb-2">
                                        {count(activeCheques.length)} in hand of {count(detail.cheques.length)}
                                    </p>
                                    <ul className="space-y-2">
                                        {detail.cheques.map(cheque => {
                                            const state = chequeState(cheque, today);
                                            return (
                                                <li key={cheque.id} className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <div className="text-[13px] text-label num truncate">
                                                            {cheque.chequeNumber || '—'}
                                                        </div>
                                                        <div className="text-[12px] text-label-3 truncate">
                                                            {cheque.bankName} · {date.short(cheque.chequeDate)}
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-none">
                                                        <div className="text-[13px] font-semibold num">
                                                            {money.compact(cheque.amount)}
                                                        </div>
                                                        <Badge tone={
                                                            state === 'cleared' ? 'pos'
                                                                : state === 'bounced' ? 'dang'
                                                                    : state === 'overdue' ? 'warn' : 'neutral'
                                                        }>
                                                            {state}
                                                        </Badge>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </>
                            )}
                        </Card>
                    )}

                    {holds(context, 'ai.generate') && (
                        <AiReportCard
                            customerId={customer.id}
                            customerName={customer.company}
                            basisNow={{
                                customerUpdatedAt: updatedAt,
                                latestActivityAt: detail.activity[0]?.createdAt ?? null,
                                cheques: holds(context, 'cheques.view')
                                    ? chequeBasis(detail.cheques.map(c => ({ status: c.status, amount: c.amount })))
                                    : null,
                            }}
                        />
                    )}
                </div>
            </div>
        </Page>
    );
}

const Detail = ({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) => (
    <div className="flex items-baseline justify-between gap-3 py-1">
        <span className="text-[12.5px] text-label-3 flex-none">{label}</span>
        <span className={cx('text-[13px] text-label text-right min-w-0 truncate', mono && 'num')}>
            {value || '—'}
        </span>
    </div>
);

/* -------------------------------------------------------------------------- */

function ActivityRow({ entry, thread }: { entry: ActivityEntry; thread: ActivityEntry[] }) {
    const { money, date } = useFormatters();

    const resolver = entry.kind === 'promise'
        ? thread.find(e => e.resolvesId === entry.id)
        : undefined;
    const state = entry.kind === 'promise' ? promiseState(entry, resolver) : null;

    return (
        <li className="px-5 py-3.5">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Badge tone={entry.kind === 'system' ? 'neutral' : 'brand'}>
                            {ACTIVITY_LABELS[entry.kind]}
                        </Badge>
                        <span className="text-[13px] font-semibold text-label">
                            {/* A departed colleague's entries still say who wrote them. */}
                            {entry.authorName || (entry.kind === 'system' ? 'Timely Payment' : 'Somebody')}
                        </span>
                        {state && (
                            <Badge tone={state === 'kept' ? 'pos' : state === 'broken' || state === 'overdue' ? 'dang' : 'warn'}>
                                {state === 'kept' ? 'Kept' : state === 'broken' ? 'Not kept'
                                    : state === 'overdue' ? 'Promise overdue' : 'Promised'}
                            </Badge>
                        )}
                    </div>
                    {entry.body && (
                        // Plain text, rendered as text. React escapes it, and
                        // nothing on this screen uses dangerouslySetInnerHTML.
                        <p className="text-[13.5px] text-label-2 mt-1 whitespace-pre-wrap break-words">
                            {entry.body}
                        </p>
                    )}
                    {entry.promisedAmount !== undefined && (
                        <p className="text-[12.5px] text-label-3 mt-1">
                            <span className="num">{money.exact(entry.promisedAmount)}</span>
                            {entry.promisedOn && <> by {date.long(entry.promisedOn)}</>}
                        </p>
                    )}
                </div>
                <time
                    dateTime={entry.createdAt}
                    className="text-[12px] text-label-3 flex-none num"
                    title={new Date(entry.createdAt).toISOString()}
                >
                    {date.short(entry.createdAt)}
                </time>
            </div>
        </li>
    );
}

/* -------------------------------------------------------------------------- */

function FollowUpForm({
    customerId, workspaceId, updatedAt, busy, onBusy, onDone, onError,
}: {
    customerId: string;
    workspaceId: string;
    updatedAt: string;
    busy: boolean;
    onBusy: (busy: boolean) => void;
    onDone: (message: string) => Promise<void>;
    onError: (message: string) => void;
}) {
    const [draft, setDraft] = useState<FollowUpDraft>(emptyDraft('note'));
    const [showProblems, setShowProblems] = useState(false);
    // The company's day, not the browser's: "not in the past" and "the day
    // after" mean the same day here as on the book and in the database.
    const { today } = useFormatters();

    const problems = draftProblems(draft, today);
    const problemFor = (field: string) =>
        showProblems ? problems.find(p => p.field === field)?.message : undefined;
    const choice = outcomeChoice(draft.kind);

    const pick = (kind: FollowUpOutcome) => {
        setShowProblems(false);
        setDraft(previous => ({
            ...previous,
            kind,
            // The form suggests a next date the moment an outcome is chosen —
            // the day after a promise, tomorrow after no answer, nothing after
            // a payment.
            nextFollowUp: suggestedNextFollowUp({ kind, promisedOn: previous.promisedOn }, today),
        }));
    };

    return (
        <Card className="mt-5 px-5 py-5">
            <h3 className="text-[15px] font-bold text-label">Record what happened</h3>
            <p className="text-[13px] text-label-3 mt-1 mb-4">
                Whoever picks this account up next reads what you write here.
            </p>

            <fieldset className="mb-4" disabled={busy}>
                <legend className="text-[12.5px] font-semibold text-label mb-2">Outcome</legend>
                <div className="flex flex-wrap gap-2">
                    {OUTCOME_CHOICES.map(option => (
                        <button
                            key={option.kind}
                            type="button"
                            aria-pressed={draft.kind === option.kind}
                            title={option.hint}
                            onClick={() => pick(option.kind)}
                            className={cx(
                                'px-3 h-9 rounded-full text-[13px] font-semibold border transition-colors',
                                'outline-none focus-visible:ring-2 focus-visible:ring-accent',
                                draft.kind === option.kind
                                    ? 'bg-accent text-on-accent border-accent'
                                    : 'bg-card text-label-2 border-separator-strong hover:text-label',
                            )}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <p className="text-[12px] text-label-3 mt-2">{choice?.hint}</p>
            </fieldset>

            <fieldset disabled={busy}>
                <label htmlFor="follow-body" className="block text-[12.5px] font-semibold text-label mb-1">
                    What was said
                </label>
                <textarea
                    id="follow-body"
                    rows={3}
                    value={draft.body}
                    aria-invalid={problemFor('body') ? true : undefined}
                    onChange={e => setDraft({ ...draft, body: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-[11px] bg-card border border-separator-strong text-[14px] text-label outline-none focus:border-accent resize-y"
                />
                {problemFor('body') && (
                    <div className="text-[12.5px] text-dang mt-1" role="alert">{problemFor('body')}</div>
                )}

                {choice?.needsPromise && (
                    <div className="grid gap-3 sm:grid-cols-2 mt-3">
                        <Field
                            id="promise-amount" label="How much" type="number" inputMode="decimal"
                            value={draft.promisedAmount} error={problemFor('promisedAmount')}
                            onChange={value => setDraft({ ...draft, promisedAmount: value })}
                        />
                        <Field
                            id="promise-date" label="By when" type="date"
                            value={draft.promisedOn} error={problemFor('promisedOn')}
                            onChange={value => setDraft({
                                ...draft,
                                promisedOn: value,
                                nextFollowUp: suggestedNextFollowUp({ kind: draft.kind, promisedOn: value }, today),
                            })}
                        />
                    </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2 mt-3">
                    <Field
                        id="next-follow-up" label="Next follow-up" type="date"
                        value={draft.nextFollowUp} error={problemFor('nextFollowUp')}
                        disabled={draft.clearFollowUp}
                        onChange={value => setDraft({ ...draft, nextFollowUp: value })}
                    />
                    <div className="flex flex-col justify-end gap-2 pb-1">
                        <label className="flex items-center gap-2.5 text-[13px] text-label min-h-[36px] cursor-pointer">
                            <input
                                type="checkbox" className="h-5 w-5 flex-none"
                                checked={draft.clearFollowUp}
                                onChange={e => setDraft({
                                    ...draft,
                                    clearFollowUp: e.target.checked,
                                    nextFollowUp: e.target.checked ? '' : draft.nextFollowUp,
                                })}
                            />
                            No further follow-up needed
                        </label>
                        <label className="flex items-center gap-2.5 text-[13px] text-label min-h-[36px] cursor-pointer">
                            <input
                                type="checkbox" className="h-5 w-5 flex-none"
                                checked={draft.completed}
                                onChange={e => setDraft({ ...draft, completed: e.target.checked })}
                            />
                            The money has been collected
                        </label>
                    </div>
                </div>
            </fieldset>

            <div className="mt-5">
                <Button
                    variant="primary"
                    disabled={busy}
                    onClick={() => {
                        if (problems.length > 0) { setShowProblems(true); return; }
                        onBusy(true);
                        setShowProblems(false);
                        void recordFollowUp(
                            followUpCall(workspaceId, customerId, draft, { expectedUpdatedAt: updatedAt }) as unknown as Record<string, unknown>,
                        )
                            .then(async () => {
                                setDraft(emptyDraft('note'));
                                await onDone('Recorded.');
                            })
                            .catch((error: Error) => onError(error.message))
                            .finally(() => onBusy(false));
                    }}
                >
                    {busy ? 'Saving…' : 'Record'}
                </Button>
            </div>
        </Card>
    );
}

function Field({
    id, label, type, value, error, disabled, inputMode, onChange,
}: {
    id: string; label: string; type: string; value: string;
    error?: string | undefined; disabled?: boolean;
    inputMode?: 'decimal'; onChange: (value: string) => void;
}) {
    return (
        <div>
            <label htmlFor={id} className="block text-[12.5px] font-semibold text-label mb-1">{label}</label>
            <input
                id={id} type={type} value={value} disabled={disabled}
                {...(inputMode ? { inputMode } : {})}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `${id}-error` : undefined}
                onChange={e => onChange(e.target.value)}
                className={cx(
                    // A ring as well as the border: a one-pixel colour change
                    // is not enough to find the field a keyboard is on.
                    'w-full h-10 px-3.5 rounded-[10px] bg-card border text-[14px] text-label outline-none focus:border-accent focus-visible:ring-2 focus-visible:ring-accent/40',
                    error ? 'border-dang' : 'border-separator-strong',
                    disabled && 'opacity-50',
                )}
            />
            {error && <div id={`${id}-error`} className="text-[12.5px] text-dang mt-1" role="alert">{error}</div>}
        </div>
    );
}

/**
 * Working down a list without going back to it.
 *
 * The efficient loop is: open the first account, record what happened, move to
 * the next. Returning to the list between every customer is a press and a scroll
 * position to lose, forty times a morning.
 *
 * The sequence is the person's own filtered list, so "next" can only ever be an
 * account they were already looking at.
 */
function Walk({
    neighbours, slug, search,
}: {
    neighbours: Neighbours;
    slug: string;
    search: string;
}) {
    const to = (id: string) => `/w/${slug}/book/${id}${search ? `?${search}` : ''}`;
    const suffix = search ? ` (${neighbours.position} of ${neighbours.total})` : '';

    return (
        <div className="flex items-center gap-2">
            <span className="text-[12.5px] text-label-3 num hidden sm:inline">
                {neighbours.position} of {neighbours.total}
            </span>
            {neighbours.previous ? (
                <Link
                    to={to(neighbours.previous.id)}
                    aria-label={`Previous account: ${neighbours.previous.company}${suffix}`}
                    className="inline-flex items-center justify-center min-h-[40px] px-3 rounded-[10px] border border-separator-strong text-[13px] font-semibold text-label hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                    ← Previous
                </Link>
            ) : (
                <span className="inline-flex items-center justify-center min-h-[40px] px-3 text-[13px] text-label-3">
                    ← Previous
                </span>
            )}
            {neighbours.next ? (
                <Link
                    to={to(neighbours.next.id)}
                    aria-label={`Next account: ${neighbours.next.company}${suffix}`}
                    className="inline-flex items-center justify-center min-h-[40px] px-3 rounded-[10px] border border-separator-strong text-[13px] font-semibold text-label hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                    Next →
                </Link>
            ) : (
                <span className="inline-flex items-center justify-center min-h-[40px] px-3 text-[13px] text-label-3">
                    Next →
                </span>
            )}
        </div>
    );
}
